import React, { useEffect, useState } from 'react';
import swal from 'sweetalert';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import StarRating from '../../UI/StarRating';
import NumberRating from '../../UI/NumberRating';
import { NepaliDatePicker } from 'nepali-calander';
import { axiosPost, axiosGet } from '../../../utils/AxiosApi';
import { CONTEXT_CONFIG, URL } from '../../../utils/Constants';
import '../../../assets/scss/_dynamicform.scss';

const animatedComponents = makeAnimated();

// const CONTEXT_CONFIG = {
//   student: {
//     teacher: { fields: ["teacher", "subject"], color: "#0ea5e9" },
//     student: { fields: ["class", "student"], color: "#8b5cf6" },
//   },
//   teacher: {
//     student: { fields: ["class", "student", "subject"], color: "#10b981" },
//     teacher: { fields: ["teacher", "subject"], color: "#f59e0b" },
//   },
//   management: {
//     teacher: { fields: ["teacher", "subject", "nextTermTarget"], color: "#ef4444" },
//     student: { fields: ["class", "student"], color: "#6366f1" },
//     management: { fields: ["managementMember"], color: "#64748b" },
//   },
// };

const FIELD_META = {
  teacher: { label: "Teacher", dataKey: "teachers", placeholder: "Select Teacher" },
  subject: { label: "Subject", dataKey: "subjects", placeholder: "Select Subject" },
  class: { label: "Class", dataKey: "classes", placeholder: "Select Class" },
  student: { label: "Student", dataKey: "students", placeholder: "Select Student" },
  managementMember: { label: "Management Member", dataKey: "managementMembers", placeholder: "Select Member" },
};

const selectStyles = (color) => ({
  control: (b) => ({
    ...b,
    borderRadius: 8,
    borderColor: "#e2e8f0",
    minHeight: 38,
    fontSize: "0.875rem",
    boxShadow: "none",
    "&:hover": { borderColor: color },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, borderRadius: 8 }),
});

const ResponseContextFields = ({
  config, contextData, contextSelections, onContextChange,
  nextTermTarget, onNextTermTargetChange, disabled, contextErrors, setContextErrors
}) => {
  if (!config) return null;
  const { fields, color } = config;

  return (
    <div className="df-context-card" style={{ borderLeftColor: color }}>
      <div className="df-context-card__header" style={{ color }}>
        Evaluation Context
      </div>
      <div className="df-context-card__body">
        {fields.map((field) => {
          if (field === "nextTermTarget") return null;
          const meta = FIELD_META[field];
          if (!meta) return null;
          const hasError = contextErrors?.has(field);
          return (
            <div className={`df-context-card__field${hasError ? " df-context-card__field--error" : ""}`} key={field} data-context-field={field}>
              <label className="df-label">
                {meta.label} <span className="df-required-star">*</span>
              </label>
              <Select
                classNamePrefix="react-select"
                components={animatedComponents}
                value={contextSelections[field] || null}
                options={contextData[meta.dataKey] || []}
                onChange={(v) => {
                  onContextChange(field, v);
                  if (v) setContextErrors((prev) => {
                    const next = new Set(prev); next.delete(field); return next;
                  });
                }}
                placeholder={meta.placeholder}
                isDisabled={disabled}
                isClearable
                styles={selectStyles(color)}
              />
            </div>
          );
        })}

        {fields.includes("nextTermTarget") && (
          <div className={`df-context-card__field${contextErrors?.has("nextTermTarget") ? " df-context-card__field--error" : ""}`} data-context-field="nextTermTarget">
            <label className="df-label">
              Next Term Target <span style={{ color }}>(%)</span>{" "}
              <span className="df-required-star">*</span>
            </label>
            <div
              className={`df-context-card__percent-input${contextErrors?.has("nextTermTarget") ? " df-context-card__percent-input--error" : ""}`}
              style={{ "--accent": color }}
            >
              <input
                type="number"
                min={0}
                max={100}
                placeholder="e.g. 85"
                value={nextTermTarget}
                onChange={(e) => {
                  onNextTermTargetChange(e);
                  if (e.target.value > 0) setContextErrors((prev) => {
                    const next = new Set(prev); next.delete("nextTermTarget"); return next;
                  });
                }}
                disabled={disabled}
                isClearable
                styles={selectStyles(color)}
              />
              <span style={{ background: color }}>%</span>
            </div>
            {nextTermTarget > 0 && (
              <div className="df-context-card__progress">
                <div
                  className="df-context-card__progress-bar"
                  style={{ width: `${Math.min(nextTermTarget, 100)}%`, background: color }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DynamicFormDisplay = ({ title, description, items, previewOnly, formMeta }) => {
  const isPreviewOnly = previewOnly || false;
  const respondentRole = formMeta?.respondentRole || null;
  const evaluateeRole = formMeta?.evaluateeRole || null;
  const formId = formMeta?.id || null;

  const config = respondentRole && evaluateeRole
    ? CONTEXT_CONFIG[respondentRole]?.[evaluateeRole] || null
    : null;
  const effectiveConfig = config || CONTEXT_CONFIG.student.teacher;

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [contextSelections, setContextSelections] = useState({});
  const [nextTermTarget, setNextTermTarget] = useState("");
  const [contextData, setContextData] = useState({
    teachers: [], subjects: [], classes: [], students: [], managementMembers: [],
  });
  // One Set for context field keys ("teacher", "subject", "nextTermTarget" …)
  const [contextErrors, setContextErrors] = useState(new Set());

  // One Set for question ids
  const [questionErrors, setQuestionErrors] = useState(new Set());

  useEffect(() => {
    if (isPreviewOnly || !formId) return;
    axiosGet(`${URL.getAvailableEvaluatees}?formId=${formId}`, (response) => {
      if (response.status === 200) {
        const data = response.data.data;
        setContextData({
          teachers: data.filter(e => e.peopleId).map(e => ({ value: e.peopleId, label: e.peopleName,subjects:e.subjects })),
          classes: [...new Map(data.filter(e => e.yearLevelId).map(e => [e.yearLevelId, { value: e.yearLevelId, label: e.yearLevelName }])).values()],
          students: [],
          managementMembers: [],
        });
      }
    });
  }, [isPreviewOnly, formId]);

  const setAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));
  const handleContextChange = (field, val) => {
    setContextSelections((prev) => ({ ...prev, [field]: val }))
    if (field === 'teacher'){
      setContextData(prev => ({
        ...prev,
        subjects: [
          ...new Map(
            val?.subjects
              ?.filter(e => e.subjectId)
              .map(e => [
                e.subjectId,
                { value: e.subjectId, label: e.subjectName }
              ])
          ).values()
        ]
      }));
    }
  }
  ;

  const validateContext = () => {
    const fields = config?.fields || [];
    const errors = new Set();

    for (const field of fields) {
      if (field === "nextTermTarget") {
        if (!nextTermTarget || nextTermTarget <= 0) errors.add("nextTermTarget");
      } else if (!contextSelections[field]) {
        errors.add(field);
      }
    }

    setContextErrors(errors); // highlight every bad field at once

    if (errors.size > 0) {
      // Scroll to the first broken context field in DOM order
      const firstKey = [...errors][0];
      const el = document.querySelector(`[data-context-field="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    const contextOk = validateContext();
    const questions = items.filter((it) => it.category === "question");
    const missing = questions.filter(
      (q) => q.required && !answers[q.id] && answers[q.id] !== 0
    );

    // Build the question error set regardless of context result
    const qErrors = new Set(missing.map((q) => q.id));
    setQuestionErrors(qErrors);
    if (!contextOk) return;

    if (qErrors.size > 0) {
      // Scroll to first missing question
      const firstId = [...qErrors][0];
      const el = document.querySelector(`[data-id="${firstId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const submittedAnswers = questions.map((q) => ({
      questionId: q.id.toString(),
      textValue: ["rating", "number"].includes(q.type) ? null : answers[q.id],
      numericValue: ["rating", "number"].includes(q.type) ? answers[q.id] : null,
    }));
    // if (config?.fields.includes("nextTermTarget") && nextTermTarget) {
    //   submittedAnswers.push({ questionId: "nextTermTarget", textValue: nextTermTarget, numericValue: null });
    // }
    const param = {
      formId,
      evaluatee: contextSelections.teacher?.value || contextSelections.student?.value || contextSelections.managementMember?.value || null,
      evaluationSubjectId: contextSelections.subject?.value || null,
      evaluationYearLevelId: contextSelections.class?.value || null,
      nextTermTarget: config?.fields.includes("nextTermTarget") ? nextTermTarget : null,
      answers: submittedAnswers,
    };
    axiosPost(URL.insertDynamicFormResponse, param, (response) => {
      if (response.status === 200) {
        setSubmitted(true);
      } else {
        swal("Error", "Failed to submit. Please try again.");
      }
    });
  };

  if (submitted) {
    return (
      <div className="df-display">
        <div className="df-display__success">
          <h4>{title || "Form"}</h4>
          <p>Your response has been recorded.</p>
          <button
            className="df-btn df-btn--ghost df-btn--sm"
            style={{ marginTop: 20 }}
            onClick={() => { setSubmitted(false); setAnswers({}); setContextSelections({}); setNextTermTarget(""); }}
          >
            Submit another response
          </button>
        </div>
      </div>
    );
  }
  const onNextTermTargetChange = (e) => {
    const raw = e.target.value;

    if (raw === "") {
      setNextTermTarget("");
      return;
    }

    let value = Number(raw);

    if (Number.isNaN(value)) return;

    value = Math.max(0, Math.min(100, value));

    setNextTermTarget(value);

  }

  return (
    <div className="df-display">
      <div className="df-display__wrapper">
        {/* Header */}
        <div className="df-display__header-card">
          <h2>{title || "Untitled Form"}</h2>
          {description && <p>{description}</p>}
          <hr />
          <span className="df-display__header-card__note">* Indicates required question</span>
        </div>

        {/* Context selectors */}
        {/* {!isPreviewOnly && ( */}
        {!!effectiveConfig.fields.length
          &&
          <ResponseContextFields
            config={effectiveConfig}
            contextData={contextData}
            contextSelections={contextSelections}
            onContextChange={handleContextChange}
            nextTermTarget={nextTermTarget}
            onNextTermTargetChange={onNextTermTargetChange}
            disabled={false}
            contextErrors={contextErrors}
          setContextErrors={setContextErrors}
          />
        }
        {/* )} */}

        {/* Items */}
        {items.map((it) =>
          it.category === "section" ? (
            <div key={it.id} className="df-display__section-card">
              <div className="df-display__section-title">{it.label || "Untitled Section"}</div>
              {it.description && <div className="df-display__section-desc">{it.description}</div>}
            </div>
          ) : (
              <div key={it.id} data-id={it.id} className={`df-display__question-card${questionErrors.has(it.id) ? " df-question-item--error" : ""}`}>
              <label className="df-display__question-card__label">
                {it.label || "Untitled Question"}
                {it.required && <span className="df-required-star">*</span>}
              </label>

              {(it.type === "short_answer" || it.type === "paragraph") && (
                <input
                  type="text"
                  className="df-display__answer-input"
                  placeholder="Your answer"
                  value={answers[it.id] || ""}
                  onChange={(e) => setAnswer(it.id, e.target.value)}
                  disabled={isPreviewOnly}
                />
              )}

              {it.type === "multiple_choice" && (
                <div className="df-display__choice-list">
                  {it.options?.map((opt) => (
                    <label key={opt.id}>
                      <input
                        type="radio"
                        name={it.id}
                        checked={answers[it.id] === opt.id}
                        onChange={() => setAnswer(it.id, opt.id)}
                        disabled={isPreviewOnly}
                      />
                      <span>{opt.text}</span>
                    </label>
                  ))}
                </div>
              )}

              {it.type === "checkboxes" && (
                <div className="df-display__choice-list">
                  {it.options?.map((opt) => (
                    <label key={opt.id}>
                      <input
                        type="checkbox"
                        checked={(answers[it.id] || []).includes(opt.id)}
                        onChange={(e) => {
                          const current = answers[it.id] || [];
                          setAnswer(it.id, e.target.checked
                            ? [...current, opt.id]
                            : current.filter((id) => id !== opt.id));
                        }}
                        disabled={isPreviewOnly}
                      />
                      <span>{opt.text}</span>
                    </label>
                  ))}
                </div>
              )}

              {it.type === "dropdown" && (
                <select
                  className="df-select"
                  value={answers[it.id] || ""}
                  onChange={(e) => setAnswer(it.id, e.target.value)}
                  disabled={isPreviewOnly}
                >
                  <option value="">Choose…</option>
                  {it.options?.map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
                </select>
              )}

              {it.type === "number" && (
                <NumberRating
                  disabled={isPreviewOnly}
                  keyId={it.id}
                  value={answers[it.id] || 0}
                  max={it.maxNumber || 5}
                  onChange={(v) => setAnswer(it.id, v)}
                />
              )}

              {it.type === "rating" && (
                <StarRating
                  disabled={isPreviewOnly}
                  value={answers[it.id] || 0}
                  max={it.maxNumber || 5}
                  onChange={(v) => setAnswer(it.id, v)}
                />
              )}

              {it.type === "date" && (
                <NepaliDatePicker
                  disabled={isPreviewOnly}
                  value={answers[it.id] || ""}
                  showToggle={true}
                  onDateSelect={(date) => setAnswer(it.id, date.ad.date)}
                />
              )}
            </div>
          )
        )}

        {/* Actions */}
        {!isPreviewOnly && (
          <div className="df-display__footer">
            <button className="df-btn df-btn--primary" onClick={handleSubmit}>
              Submit
            </button>
            <button
              className="df-btn df-btn--ghost df-btn--sm"
              onClick={() => { setAnswers({}); setContextSelections({}); setNextTermTarget(""); }}
            >
              Clear form
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicFormDisplay;